/* eslint-disable ember/no-classic-components */
import Component, { Input } from "@ember/component";
import { fn, hash } from "@ember/helper";
import { on } from "@ember/modifier";
import { action, computed } from "@ember/object";
import { isEmpty } from "@ember/utils";
import { tagName } from "@ember-decorators/component";
import DatePickerFuture from "discourse/components/date-picker-future";
import withEventValue from "discourse/helpers/with-event-value";
import {
  extendedDefaultTimeShortcuts,
  formatTime,
  hideDynamicTimeShortcuts,
  TIME_SHORTCUT_TYPES,
  timeShortcuts,
} from "discourse/lib/time-shortcut";
import FutureDateInputSelector, {
  FORMAT,
} from "discourse/select-kit/components/future-date-input-selector";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";

@tagName("")
export default class DFutureDateInput extends Component {
  /** @type {string | null | undefined} */
  selection = null;
  includeDateTime = true;

  /** @type {string | null} */
  displayLabel = null;

  /** @type {string | null | undefined} */
  labelClasses = null;

  /** @type {string | null | undefined} */
  userTimezone = null;

  /** @type {((value: string | null) => void) | null | undefined} */
  onChangeInput = null;

  /** @type {string | null} */
  _date = null;

  /** @type {string | null} */
  _time = null;

  init() {
    super.init(...arguments);
    this.userTimezone = this.currentUser.user_option.timezone;
  }

  @computed("selection")
  get isCustom() {
    return this.selection === "custom";
  }

  @computed("includeDateTime", "isCustom")
  get displayDateAndTimePicker() {
    return this.includeDateTime && this.isCustom;
  }

  @computed("_date")
  get timeInputDisabled() {
    return isEmpty(this._date);
  }

  @computed("customShortcuts")
  get shortcuts() {
    let shortcuts;
    if (this.customShortcuts && this.customShortcuts.length) {
      shortcuts = this.customShortcuts;
    } else {
      shortcuts = extendedDefaultTimeShortcuts(this.userTimezone);
    }

    const shortcutsFactory = timeShortcuts(this.userTimezone);
    if (this.includeDateTime) {
      shortcuts.push(shortcutsFactory.custom());
    }
    if (this.includeNow) {
      shortcuts.push(shortcutsFactory.now());
    }

    shortcuts = hideDynamicTimeShortcuts(
      shortcuts,
      this.userTimezone,
      this.siteSettings
    );

    return shortcuts.map((s) => {
      return {
        id: s.id,
        name: i18n(s.label),
        time: s.time,
        timeFormatted: formatTime(s),
        icon: s.icon,
      };
    });
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);

    if (this.label) {
      this.set("displayLabel", i18n(this.label));
    }

    if (this.input) {
      const dateTime = moment(this.input);
      const closestShortcut = this._findClosestShortcut(dateTime);
      if (!this.noRelativeOptions && closestShortcut) {
        this.set("selection", closestShortcut.id);
      } else {
        this.setProperties({
          selection: TIME_SHORTCUT_TYPES.CUSTOM,
          _date: dateTime.format("YYYY-MM-DD"),
          _time: dateTime.format("HH:mm"),
        });
      }
    }
  }

  @action
  onChangeDate(date) {
    if (!date) {
      this.set("_time", null);
    }

    this._dateTimeChanged(date, this._time);
  }

  @action
  onChangeTime(time) {
    if (this._date) {
      this._dateTimeChanged(this._date, time);
    }
  }

  _dateTimeChanged(date, time) {
    time = time ? ` ${time}` : "";
    const dateTime = moment(`${date}${time}`);

    if (dateTime.isValid()) {
      this.onChangeInput?.(dateTime.format(FORMAT));
    } else {
      this.onChangeInput?.(null);
    }
  }

  _findClosestShortcut(dateTime) {
    return this.shortcuts.find((tf) => {
      if (tf.time) {
        const diff = tf.time.diff(dateTime);
        return 0 <= diff && diff < 60 * 1000;
      }
    });
  }

  <template>
    <div class="future-date-input" ...attributes>
      {{#unless this.noRelativeOptions}}
        <div class="control-group">
          <label class={{this.labelClasses}}>
            {{#if this.displayLabelIcon}}{{dIcon
                this.displayLabelIcon
              }}{{/if}}{{this.displayLabel}}
          </label>
          <FutureDateInputSelector
            @clearable={{this.clearable}}
            @content={{this.shortcuts}}
            @onChange={{fn (mut this.selection)}}
            @onChangeInput={{this.onChangeInput}}
            @options={{hash none="time_shortcut.select_timeframe"}}
            @value={{readonly this.selection}}
          />
        </div>
      {{/unless}}

      {{#if this.displayDateAndTimePicker}}
        <div class="control-group future-date-input-date-picker">
          {{dIcon "calendar-days"}}
          <DatePickerFuture
            @defaultDate={{this._date}}
            @onSelect={{this.onChangeDate}}
            @value={{this._date}}
          />
        </div>

        <div class="control-group future-date-input-time-picker">
          {{dIcon "far-clock"}}
          <Input
            class="time-input"
            disabled={{this.timeInputDisabled}}
            placeholder="--:--"
            @type="time"
            @value={{this._time}}
            {{on "input" (withEventValue this.onChangeTime)}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
